import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Layout } from '../components/Layout';
import { SEO } from '../components/SEO';
import { Calendar, MapPin, Users, ArrowRight, Trophy, BookOpen, Users2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper to parse date without timezone issues
const formatEventDate = (dateStr, options = {}) => {
  if (!dateStr) return '';
  const [year, month, day] = dateStr.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    ...options
  });
};

const DEFAULT_HERO_IMAGE = "https://images.unsplash.com/photo-1661260100858-718952c5b5ae?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxob3JzZSUyMHJpZGVycyUyMGNvbW11bml0eSUyMGdhdGhlcmluZyUyMG91dGRvb3J8ZW58MHx8fHwxNzY4NjczMjQzfDA&ixlib=rb-4.1.0&q=85";
const DEFAULT_ABOUT_IMAGE = "https://images.unsplash.com/photo-1712194291074-46300e5cacb5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHw0fHxob3JzZSUyMHJpZGVycyUyMGNvbW11bml0eSUyMGdhdGhlcmluZyUyMG91dGRvb3J8ZW58MHx8fHwxNzY4NjczMjQzfDA&ixlib=rb-4.1.0&q=85";

const eventTypeConfig = {
  show: { icon: Trophy, label: 'Horse Show', color: 'bg-amber-100 text-amber-800' },
  clinic: { icon: BookOpen, label: 'Clinic', color: 'bg-blue-100 text-blue-800' },
  meetup: { icon: Users2, label: 'Meetup', color: 'bg-green-100 text-green-800' },
};

const EventCard = ({ event }) => {
  const navigate = useNavigate();
  const config = eventTypeConfig[event.event_type] || eventTypeConfig.show;
  const Icon = config.icon;
  const spotsLeft = event.max_participants - event.current_participants;
  
  return (
    <Card 
      className="group overflow-hidden border border-stone-200 hover:shadow-lg hover:border-primary/20 transition-all duration-300 cursor-pointer"
      onClick={() => navigate(`/events/${event.id}`)}
      data-testid={`event-card-${event.id}`}
    >
      <div className="relative h-48 overflow-hidden">
        <img
          src={event.image_url || DEFAULT_HERO_IMAGE}
          alt={event.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute top-4 left-4">
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${config.color} flex items-center gap-1`}>
            <Icon className="w-4 h-4" />
            {config.label}
          </span>
        </div>
      </div>
      <CardContent className="p-6">
        <h3 className="font-display text-xl font-semibold text-stone-900 mb-2 group-hover:text-primary transition-colors">
          {event.title}
        </h3>
        <p className="text-stone-600 text-sm mb-4 line-clamp-2">{event.description}</p>
        <div className="flex flex-col gap-2 text-sm text-stone-500">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-primary" />
            <span>{formatEventDate(event.date)} at {event.time}</span>
          </div>
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-primary" />
            <span>{event.location}</span>
          </div>
          <div className="flex items-center justify-between mt-2">
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-primary" />
              <span className={spotsLeft <= 5 ? 'text-terracotta font-medium' : ''}>
                {spotsLeft} spots left
              </span>
            </div>
            <span className="font-mono text-lg font-semibold text-primary">
              {event.event_type === 'show' ? '$5 CAD/class' : `$${event.price}`}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export const HomePage = () => {
  const navigate = useNavigate();
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState({
    hero_title: "Where Champions Begin Their Journey",
    hero_subtitle: "Join our prestigious equestrian community. Register for horse shows, clinics, and exclusive events that celebrate the bond between rider and horse.",
    hero_image: DEFAULT_HERO_IMAGE,
    about_title: "A Legacy of Excellence in Equestrian Sports",
    about_text: "For over two decades, Manitoulin Horse Enthusiasts Club has been the cornerstone of equestrian excellence in our community.",
    about_image: DEFAULT_ABOUT_IMAGE,
    cta_title: "Ready to Start Your Journey?",
    cta_text: "Join Manitoulin Horse Enthusiasts Club today and become part of a community that shares your passion for horses and equestrian excellence."
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [eventsRes, contentRes] = await Promise.all([
          axios.get(`${API}/events`),
          axios.get(`${API}/homepage-content`).catch(() => ({ data: null }))
        ]);
        setUpcomingEvents(eventsRes.data.slice(0, 3));
        if (contentRes.data) {
          setContent(contentRes.data);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Split hero title for styling
  const titleParts = content.hero_title.split('\n');
  const mainTitle = titleParts[0] || content.hero_title;
  const subTitle = titleParts[1] || '';

  return (
    <Layout>
      <SEO 
        title={null}
        description="Join the Manitoulin Horse Enthusiasts Club (MHEC) - your premier equestrian community on Manitoulin Island. Register for horse shows, clinics, and events."
        url="/"
      />
      {/* Hero Section */}
      <section className="relative h-[85vh] min-h-[600px] flex items-center">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${content.hero_image || DEFAULT_HERO_IMAGE})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-hunter/40 via-hunter/60 to-hunter/80" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <h1 
            className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 animate-fade-in"
            style={{ animationDelay: '0.1s' }}
          >
            {mainTitle}
            {subTitle && (
              <>
                <br />
                <span className="text-beige">{subTitle}</span>
              </>
            )}
          </h1>
          <p 
            className="text-lg text-stone-200 max-w-2xl mx-auto mb-10 animate-fade-in"
            style={{ animationDelay: '0.2s' }}
          >
            {content.hero_subtitle}
          </p>
          <div 
            className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in"
            style={{ animationDelay: '0.3s' }}
          >
            <Button 
              size="lg" 
              className="bg-terracotta hover:bg-terracotta-light text-white px-8 py-6 text-lg"
              onClick={() => navigate('/events')}
              data-testid="hero-browse-events-btn"
            >
              Browse Events
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="border-2 border-white text-white hover:bg-white hover:text-hunter px-8 py-6 text-lg"
              onClick={() => navigate('/register')}
              data-testid="hero-join-btn"
            >
              Join Our Club
            </Button>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-secondary">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { value: '500+', label: 'Members' },
              { value: '50+', label: 'Events/Year' },
              { value: '25', label: 'Years Legacy' },
              { value: '100%', label: 'Passion' },
            ].map((stat, idx) => (
              <div key={idx} className="text-center">
                <div className="font-display text-4xl font-bold text-primary mb-2">{stat.value}</div>
                <div className="text-stone-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
            <div className="lg:col-span-5">
              <div className="relative">
                <img 
                  src={content.about_image || DEFAULT_ABOUT_IMAGE} 
                  alt="Horse in field" 
                  className="rounded-lg shadow-xl"
                />
                <div className="absolute -bottom-6 -right-6 w-48 h-48 bg-primary/10 rounded-lg -z-10" />
              </div>
            </div>
            <div className="lg:col-span-7">
              <span className="text-terracotta font-medium uppercase tracking-wider text-sm">About Our Club</span>
              <h2 className="font-display text-3xl lg:text-4xl font-bold text-stone-900 mt-4 mb-6">
                {content.about_title}
              </h2>
              <div className="text-stone-600 text-lg mb-8 leading-relaxed whitespace-pre-line">
                {content.about_text}
              </div>
              <Button 
                className="bg-primary hover:bg-primary/90"
                onClick={() => navigate('/register')}
                data-testid="about-join-btn"
              >
                Become a Member
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Upcoming Events Section */}
      <section className="py-24 bg-secondary">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12">
            <div>
              <span className="text-terracotta font-medium uppercase tracking-wider text-sm">Don't Miss Out</span>
              <h2 className="font-display text-3xl lg:text-4xl font-bold text-stone-900 mt-4">
                Upcoming Events
              </h2>
            </div>
            <Button 
              variant="outline" 
              className="mt-4 md:mt-0 border-primary text-primary hover:bg-primary hover:text-white"
              onClick={() => navigate('/events')}
              data-testid="view-all-events-btn"
            >
              View All Events
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <Card key={i} className="h-96 animate-pulse bg-stone-100" />
              ))}
            </div>
          ) : upcomingEvents.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {upcomingEvents.map((event) => (
                <EventCard key={event.id} event={event} />
              ))}
            </div>
          ) : (
            <Card className="p-12 text-center">
              <Calendar className="w-12 h-12 text-stone-300 mx-auto mb-4" />
              <h3 className="font-display text-xl font-semibold text-stone-700 mb-2">
                No Upcoming Events
              </h3>
              <p className="text-stone-500">
                Check back soon for new events!
              </p>
            </Card>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="bg-primary rounded-2xl p-12 md:p-16 text-center relative overflow-hidden">
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 right-0 w-96 h-96 bg-white rounded-full -translate-y-1/2 translate-x-1/2" />
              <div className="absolute bottom-0 left-0 w-64 h-64 bg-white rounded-full translate-y-1/2 -translate-x-1/2" />
            </div>
            <div className="relative">
              <h2 className="font-display text-3xl lg:text-4xl font-bold text-white mb-6">
                {content.cta_title}
              </h2>
              <p className="text-stone-200 text-lg max-w-2xl mx-auto mb-8">
                {content.cta_text}
              </p>
              <Button 
                size="lg" 
                className="bg-terracotta hover:bg-terracotta-light text-white px-8"
                onClick={() => navigate('/register')}
                data-testid="cta-join-btn"
              >
                Join Now
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default HomePage;
